import React, { useMemo, useState } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  isSameMonth,
  isSameDay,
  isBefore,
  format,
} from "date-fns";

interface InlineDatePickerProps {
  selectedDate?: Date | null;
  minDate: Date;
  maxDate: Date;
  isDayEnabled?: (date: Date) => boolean;
  onSelect: (date: Date) => void;
}

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const InlineDatePicker: React.FC<InlineDatePickerProps> = ({
  selectedDate,
  minDate,
  maxDate,
  isDayEnabled,
  onSelect,
}) => {
  const initialMonth = selectedDate && !isBefore(selectedDate, minDate) ? selectedDate : minDate;
  const [currentMonth, setCurrentMonth] = useState<Date>(startOfMonth(initialMonth));

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end = endOfWeek(endOfMonth(currentMonth));
    const days: Date[] = [];
    let day = start;
    while (day <= end) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentMonth]);

  const isDisabled = (date: Date) => {
    if (isBefore(date, minDate)) return true;
    if (isBefore(maxDate, date)) return true;
    if (isDayEnabled && !isDayEnabled(date)) return true;
    return false;
  };

  const handlePrevMonth = () => setCurrentMonth(addMonths(currentMonth, -1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700">
      <div className="flex items-center justify-between p-3 border-b border-neutral-200 dark:border-neutral-700">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="px-2 py-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label="Previous month"
        >
          ‹
        </button>
        <div className="font-semibold">{format(currentMonth, "MMMM yyyy")}</div>
        <button
          type="button"
          onClick={handleNextMonth}
          className="px-2 py-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-px p-3">
        {WEEKDAY_SHORT.map((w) => (
          <div
            key={w}
            className="text-center text-xs font-medium text-neutral-600 dark:text-neutral-300"
          >
            {w}
          </div>
        ))}
        {calendarDays.map((day) => {
          const disabled = isDisabled(day);
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const selected = selectedDate ? isSameDay(day, selectedDate) : false;
          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={disabled || !isCurrentMonth}
              onClick={() => onSelect(day)}
              className={
                "h-10 text-sm rounded-md flex items-center justify-center " +
                (disabled || !isCurrentMonth
                  ? " text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
                  : " hover:bg-primary-50 dark:hover:bg-primary-900/20 cursor-pointer") +
                (selected
                  ? " bg-primary-600 text-white"
                  : " bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800")
              }
              aria-pressed={selected}
            >
              {format(day, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default InlineDatePicker;
